/* import __COLOCATED_TEMPLATE__ from './meeting-booking-funnel.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type { Options, Point, SeriesBarOptions, SeriesOptionsType } from 'highcharts';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import { use } from 'ember-resources/util/function-resource';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import {
  getCardLayout,
  SdrMeetingBookingFunnelConfig,
  SUFFIXES,
} from 'embercom/components/reporting/bespoke/sdr-performance/meeting-booking-funnel/series-config';

type RoutingOutcome = {
  identifier: string;
  title: string;
  meeting_provider?: string | null;
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
};

interface Signature {
  Args: {
    renderableChart: RenderableChart;
    openUnderlyingDataModal: (chartSeries: ChartSeries) => void;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

const SEGMENT_COLORS = {
  notBooked: PALETTE['neutral-border'],
  booked: PALETTE['vis-green-50'],
  buttonBooked: PALETTE['vis-green-30'],
};

export default class MeetingBookingFunnel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked finSdrConfig: SdrConfig | null = null;
  @tracked configLoaded = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
    } catch (e: unknown) {
      console.error('[MeetingBookingFunnel] Failed to load SDR config:', e);
      this.finSdrConfig = null;
    } finally {
      this.configLoaded = true;
    }
  }

  get bookingEnabledOutcomes(): RoutingOutcome[] {
    let outcomes = this.finSdrConfig?.routing_outcomes || [];
    return outcomes.filter((o) => Boolean(o.meeting_provider));
  }

  get chartDefinition() {
    return {
      chart_series: SdrMeetingBookingFunnelConfig.getChartSeriesWithLabels(
        this.intl,
        this.bookingEnabledOutcomes,
      ),
      view_by: null,
    };
  }

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.renderableChart.reportState,
  });

  get metrics(): Record<string, number> {
    return SdrMeetingBookingFunnelConfig.getMetricsFromChartDataRequest(
      this.dataResource.chartData,
      this.bookingEnabledOutcomes,
    );
  }

  get hasData() {
    return this.bookingEnabledOutcomes.some(
      (outcome) => (this.metrics[`${outcome.identifier}${SUFFIXES.total}`] || 0) > 0,
    );
  }

  get categories() {
    return this.bookingEnabledOutcomes.map((o) => o.title);
  }

  private metricFor(outcomeId: string, suffix: (typeof SUFFIXES)[keyof typeof SUFFIXES]): number {
    return this.metrics[`${outcomeId}${suffix}`] || 0;
  }

  get totalBooked() {
    return this.bookingEnabledOutcomes.reduce(
      (sum, o) => sum + this.metricFor(o.identifier, SUFFIXES.booked),
      0,
    );
  }

  get totalButtonBooked() {
    return this.bookingEnabledOutcomes.reduce(
      (sum, o) => sum + this.metricFor(o.identifier, SUFFIXES.buttonBooked),
      0,
    );
  }

  get buttonPercentage() {
    return this.totalBooked > 0 ? Math.round((this.totalButtonBooked / this.totalBooked) * 100) : 0;
  }

  get subtitleText() {
    if (this.totalButtonBooked > 0) {
      return this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.subtitle.with-button',
        {
          count: this.totalBooked,
          buttonCount: this.totalButtonBooked,
          pct: this.buttonPercentage,
        },
      );
    }
    return this.intl.t(
      'ai-agent.analyze.sales-performance.meeting-booking-funnel.subtitle.no-button',
      { count: this.totalBooked },
    );
  }

  get notBookedSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let total = this.metricFor(outcome.identifier, SUFFIXES.total);
      let booked = this.metricFor(outcome.identifier, SUFFIXES.booked);
      let notBooked = Math.max(total - booked, 0);
      return total > 0 ? notBooked : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.not-booked',
      ),
      data,
      color: SEGMENT_COLORS.notBooked,
    };
  }

  get bookedOrganicSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let booked = this.metricFor(outcome.identifier, SUFFIXES.booked);
      let buttonBooked = this.metricFor(outcome.identifier, SUFFIXES.buttonBooked);
      let organic = Math.max(booked - buttonBooked, 0);
      if (organic > 0) {
        return organic;
      }
      return booked > 0 ? 0 : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.total-booked',
      ),
      data,
      color: SEGMENT_COLORS.booked,
    };
  }

  get bookedViaButtonSeries(): SeriesBarOptions {
    let data = this.bookingEnabledOutcomes.map((outcome) => {
      let buttonBooked = this.metricFor(outcome.identifier, SUFFIXES.buttonBooked);
      return buttonBooked > 0 ? buttonBooked : null;
    });
    return {
      type: 'bar',
      name: this.intl.t(
        'ai-agent.analyze.sales-performance.meeting-booking-funnel.legend.booked-via-button',
      ),
      data,
      color: SEGMENT_COLORS.buttonBooked,
    };
  }

  // Stack order is significant: notBooked is series[0] so it renders at the start of each
  // horizontal bar, the organic-booked subset of "Total meetings booked" sits in the middle,
  // and the button-attributed portion is the lighter cap at the end.
  get highchartsSeries(): SeriesOptionsType[] {
    return [this.notBookedSeries, this.bookedOrganicSeries, this.bookedViaButtonSeries];
  }

  get highchartsOptions(): Options {
    // Card height (parent's grid_height) and chart height stay in sync via getCardLayout —
    // see series-config.ts for the formula and rationale.
    let { chartPx } = getCardLayout(this.bookingEnabledOutcomes.length);

    return {
      chart: {
        type: 'bar',
        height: chartPx,
        animation: false,
        marginLeft: 150,
        spacingTop: 8,
      },
      title: { text: undefined },
      xAxis: {
        categories: this.categories,
        labels: {
          style: { fontSize: '12px', fontWeight: '500' },
        },
        lineColor: PALETTE['neutral-border'],
      },
      yAxis: { visible: false },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        symbolRadius: 4,
        itemStyle: { fontSize: '12px', fontWeight: '400' },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        borderWidth: 0,
        shadow: false,
        backgroundColor: 'transparent',
        shared: true,
        formatter(this: Point) {
          let points = this.points ?? [this];
          let chart = points[0]?.series?.chart;
          let idx = points[0]?.index ?? 0;
          if (!chart) {
            return '';
          }
          let notBooked: number = (chart.series[0]?.data[idx]?.y as number | null) ?? 0;
          let organic: number = (chart.series[1]?.data[idx]?.y as number | null) ?? 0;
          let button: number = (chart.series[2]?.data[idx]?.y as number | null) ?? 0;
          let totalBooked = organic + button;
          let total = totalBooked + notBooked;
          let bookedNoun: string = chart.series[1]?.name ?? '';

          let heading = '';
          let label = '';
          if (total === 0) {
            heading = '—';
          } else if (totalBooked === 0) {
            heading = '0';
            label = bookedNoun;
          } else {
            let rate = Math.round((totalBooked / total) * 100);
            heading = `${totalBooked} / ${total} (${rate}%)`;
            label = bookedNoun;
          }

          return `<div style="background: white; border-radius: 8px; padding: 12px 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.12); min-width: 128px;">
            <div style="font: 600 14px/24px Inter, system-ui, sans-serif; color: var(--text-default); padding-bottom: 4px;">${heading}</div>
            ${label ? `<div style="font: 500 10px/135% 'JetBrains Mono', monospace; text-transform: uppercase; color: var(--text-muted);">${label}</div>` : ''}
          </div>`;
        },
      },
      plotOptions: {
        bar: {
          stacking: 'normal',
          borderRadius: 3,
          pointWidth: 20,
          states: {
            hover: { brightness: -0.05 },
            inactive: { enabled: false },
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '11px',
              fontWeight: '600',
              textOutline: 'none',
            },
            formatter(this: Point) {
              if (this.y == null) {
                return '';
              }
              let chart = this.series.chart;
              let organicSeries = chart.series[1];
              let buttonSeries = chart.series[2];
              let idx = this.index;

              // Series 1 (organic / "Total meetings booked"): show combined booked count + rate.
              if (this.series.index === 1) {
                let organic: number = this.y;
                let button: number = (buttonSeries?.data[idx]?.y as number | null) ?? 0;
                let totalBooked = organic + button;
                if (totalBooked === 0) {
                  return '';
                }
                let notBooked: number = (chart.series[0]?.data[idx]?.y as number | null) ?? 0;
                let total = totalBooked + notBooked;
                let rate = total > 0 ? Math.round((totalBooked / total) * 100) : 0;
                return `${totalBooked} (${rate}%)`;
              }
              // Series 2 (Messenger button): only label when total-booked series is hidden via legend toggle.
              if (this.series.index === 2 && this.y > 0 && !organicSeries?.visible) {
                let buttonY: number = this.y;
                let notBooked: number = (chart.series[0]?.data[idx]?.y as number | null) ?? 0;
                let total = buttonY + notBooked;
                let rate = total > 0 ? Math.round((buttonY / total) * 100) : 0;
                return `${buttonY} (${rate}%)`;
              }
              return '';
            },
          },
        },
      },
      credits: { enabled: false },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::SdrPerformance::MeetingBookingFunnel': typeof MeetingBookingFunnel;
    'reporting/bespoke/sdr-performance/meeting-booking-funnel': typeof MeetingBookingFunnel;
  }
}
